<template>
  <Layout overflow="hidden" column border class="walkthrough-designer">
    <portal-target name="main-modal" class="main-modal"></portal-target>
    <loading
      :active.sync="showLoading"
      :can-cancel="false"
      :is-full-page="true"
      background-color="#f0f0f0"
      loader="bars"
    />
    <Banner :alert="notification" @close="onBannerClose" />
    <Flex>
      <Flex :fill="true">
        <div class="font-bold capitalize opacity-50 w-tabs items-center px-2 text-lg">
          <span> {{ defaultTitle }} </span>
        </div>
      </Flex>
      <Flex justify-end>
        <Button
          color="primary"
          v-if="initialisedData"
          class="save-button"
          @click="save"
          append-icon="save"
          ><span class="save-button-label">SAVE</span>
        </Button>
      </Flex>
    </Flex>
    <Form
      class="designer-form"
      ref="designerForm"
      v-show="initialisedData"
      :values="walkthroughData"
    >
      <FormField name="name" label="Name">
        <TextField @keydown.enter.capture.prevent.stop required />
      </FormField>
      <FormField name="keyword" label="Keyword">
        <TextField @keydown.enter.capture.prevent.stop required />
      </FormField>
      <FormField name="details" label="Details">
        <TextEditor v-model="walkthroughData.details" />
      </FormField>
      <FormField name="photos" label="Photos">
        <FileList
          v-if="fileListSetupComplete && initialisedData"
          :service="fileService"
          :maxFileSize="maxFileSize"
          :acceptedExtensions="supportedImageExtensions"
        >
          <template #icon="{ file }">
            <div class="w-file-list__thumbnail" v-if="file.data.id">
              <img alt="thumbnail" :src="getThumbnailUrl(file)" />
            </div>
          </template>
        </FileList>
      </FormField>
      <FormField label="Tasks">
        <DataTable
          v-if="walkthroughData.tasks"
          toolbar
          selectable
          ref="taskTable"
          :key="uniqueId"
          :actions="[]"
          :data="getSortedDataCopy().tasks"
          :pagination="false"
          :columns="[
            { property: 'number', label: 'Number', width: '1/5', sortable: false },
            { property: 'name', label: 'Task', sortable: false },
          ]"
        >
          <template #actions>
            <TaskEditor
              :taskValidator="validateTask"
              :entryIndex="-1"
              :editorData="{ allowNA: false, steps: [] }"
              @taskModified="onTaskModified"
            />
          </template>
          <template v-slot:selection-actions="values">
            <IconButton
              src="trash"
              @click="onSelectedTaskDeleteClick(values.selectedItems)"
            ></IconButton>
          </template>
          <template #content:name="data">
            <TaskEditor
              :taskValidator="validateTask"
              :entryIndex="getEntryIndex(data)"
              :editorData="getTaskEditorData(data)"
              @taskModified="onTaskModified"
            />
          </template>
        </DataTable>
      </FormField>
      <FormField name="enabled" label="Enabled">
        <Checkbox />
      </FormField>
    </Form>
  </Layout>
</template>

<script>
import Layout from '@wizer/core/components/layout/Layout'
import Button from '@wizer/core/components/button/Button'
import Checkbox from '@wizer/core/components/checkbox/Checkbox.vue'
import Flex from '@wizer/core/components/flex/Flex.vue'
import Form from '@wizer/core/components/form/Form'
import TextField from '@wizer/core/components/text-field/TextField'
import TextEditor from '@wizer/core/components/text-editor/TextEditor'
import { mapActions, mapGetters, mapState } from 'vuex'
import Loading from 'vue-loading-overlay'
import DataTable from '@wizer/core/components/data-table/DataTable'
import IconButton from '@wizer/core/components/icon-button/IconButton'
import _ from 'lodash'
import events from '@wizer/core/events'
import FormField from '@/components/common/FormField'
import FileList from '@/components/common/FileList'
import WalkthroughDataService from '@/services/WalkthroughDataService'
import WalkthroughAttachmentService from '@/services/WalkthroughAttachmentService'
import Banner from '../components/Banner'
import AttachmentMixin from '@/mixins/AttachmentMixin'
import TaskEditor from '@/components/walkthroughs/TaskEditor'
import DataTableMixin from '@/mixins/DataTableMixin'
import PreventCloseMixin from '@/mixins/PreventCloseMixin'
import TitleMixin from '@/mixins/TitleMixin'
import store from '@/store/store'

export default {
  name: 'WalkthroughDesigner',
  title() {
    return this.defaultTitle
  },
  mixins: [AttachmentMixin, DataTableMixin, PreventCloseMixin, TitleMixin],
  components: {
    Layout,
    Button,
    Checkbox,
    Flex,
    Form,
    FormField,
    TextField,
    TextEditor,
    Banner,
    Loading,
    FileList,
    DataTable,
    TaskEditor,
    IconButton,
  },
  data() {
    return {
      defaultTitle: 'Walkthrough Designer',
      id: this.$route.params?.id,
      walkthroughData: {
        enabled: false,
        tasks: [],
      },
      showLoading: false,
      initialisedData: false,
      isDirty: false,
      fileService: WalkthroughAttachmentService,
      defaultSortColumn: 'number',
      uniqueId: _.uniqueId(),
    }
  },
  async mounted() {
    if (this.id) {
      try {
        this.showLoading = true
        const data = (await WalkthroughDataService.getTemplateData(this.id)).data
        this.walkthroughData = {
          name: data.name,
          keyword: data.keyword,
          enabled: data.enabled,
          ...JSON.parse(data.template),
        }
        await this.markDataInitDone()
      } catch (error) {
        const message = error
        const notification = { type: 'error', message }
        await this.$store.dispatch('Notification/add', notification, { root: true })
      } finally {
        this.showLoading = false
      }
    } else {
      await this.markDataInitDone()
    }
    events.subscribe('file-upload-started', () => {
      this.showLoading = true
    })
    events.subscribe('file-upload-finished', () => {
      this.showLoading = false
    })
  },
  methods: {
    getTaskEditorData(data) {
      return this.getSortedDataCopy().tasks[this.getEntryIndex(data)]
    },
    async markDataInitDone() {
      await this.$nextTick(() => {
        this.initialisedData = true
      })
    },
    onBannerClose() {
      this.removeNotification(this.notifications.slice(-1)[0])
    },
    ...mapActions({ removeNotification: 'Notification/remove' }),
    async validate() {
      await this.$nextTick(() => {
        this.$refs.designerForm.validate()
      })
    },
    getSortedDataCopy() {
      const isNumericStringFunction = (obj) => {
        // eslint-disable-next-line no-restricted-globals
        return isNaN(obj[this.defaultSortColumn])
      }
      const numericIterateeFunction = (obj) => {
        return parseFloat(obj[this.defaultSortColumn])
      }

      const dataCopy = _.cloneDeep(this.walkthroughData)
      const isNotNumericTaskNumbers = dataCopy.tasks.some(isNumericStringFunction)
      let sortedTasks
      if (isNotNumericTaskNumbers) {
        sortedTasks = _.orderBy(dataCopy.tasks, this.defaultSortColumn)
      } else {
        sortedTasks = _.orderBy(dataCopy.tasks, numericIterateeFunction)
      }
      sortedTasks.forEach((task) => {
        const isNotNumericStepNumbers = task.steps.some(isNumericStringFunction)
        if (isNotNumericStepNumbers) {
          task.steps = _.orderBy(task.steps, this.defaultSortColumn)
        } else {
          task.steps = _.orderBy(task.steps, numericIterateeFunction)
        }
      })
      dataCopy.tasks = sortedTasks
      return dataCopy
    },
    async save() {
      await this.validate()
      if (this.$refs.designerForm.numErrors) {
        return
      }
      this.showLoading = true
      let notification
      try {
        const dataCopy = this.getSortedDataCopy()
        const responseData = await WalkthroughDataService.save(this.id, dataCopy, {
          addedImages: this.addedImages,
          removedImages: this.removedImages,
        })
        this.id = responseData.data.id
        notification = {
          type: 'success',
          message: 'Walkthrough Template has been saved successfully',
          duration: this.notificationDuration,
        }
        this.isDirty = false
        await this.$store.dispatch('WalkthroughAttachments/resetImageData')
      } catch (error) {
        const message = error.response ? error.response.data : error
        notification = { type: 'error', message, duration: this.notificationDuration }
      } finally {
        this.showLoading = false
        await this.$store.dispatch('Notification/add', notification, { root: true })
      }
    },
    onTaskModified(taskData, entryIndex) {
      this.walkthroughData = this.getSortedDataCopy()
      const currentIndex = this.getEntryIndex(taskData)
      this.onElementModified(taskData, entryIndex, currentIndex, this.walkthroughData.tasks)
      this.isDirty = true
    },
    async validateTask(taskData, entryIndex) {
      const currentIndex = this.getEntryIndex(taskData)
      const errorMsg = 'A Task With Such Number Already Exists'
      return this.validateEntry(taskData, entryIndex, currentIndex, errorMsg)
    },
    getEntryIndex(taskData) {
      return this.getElementIndex(taskData, this.getSortedDataCopy().tasks, 'number')
    },
    onSelectedTaskDeleteClick(selectedItems) {
      const callbackOnConfirm = () => {
        selectedItems
          .flatMap((item) => item.photos)
          .forEach(async (photo) =>
            store.dispatch('WalkthroughAttachments/addRemovedImage', photo.id, {
              root: true,
            }),
          )
        this.uniqueId = _.uniqueId()
      }
      events.confirm(async () => {
        this.onDeleteClick(selectedItems, 'name', this.walkthroughData, 'tasks', 'taskTable')
        callbackOnConfirm()
      }, 'Deleting a task will remove the contained images from all existing, completed Walkthrough records. \n\n Do you want to continue?')
    },
  },
  computed: {
    tasks() {
      return this.walkthroughData.tasks || []
    },
    notification() {
      if (!this.notifications.length) return null
      return this.notifications.slice(-1)[0]
    },
    ...mapGetters('WalkthroughAttachments', ['addedImages', 'removedImages']),
    ...mapState({
      notifications: (state) => state.Notification.notifications,
      notificationDuration: (state) => state.Notification.notificationDuration,
    }),
    // Only used for the purposes of marking the form dirty in the watch block, as otherwise
    // newVal and oldVal for this.walkthroughData are always the same in the watcher
    computedWalkthroughData() {
      return { ...this.walkthroughData }
    },
  },
  watch: {
    computedWalkthroughData: {
      handler(newVal, oldVal) {
        if (newVal.name) {
          this.updateTitle(`Walkthrough - ${newVal.name}`)
        } else {
          this.updateTitle(this.defaultTitle)
        }
        if (this.initialisedData && !_.isEqual(JSON.stringify(newVal), JSON.stringify(oldVal))) {
          this.isDirty = true
        }
      },
      deep: true,
    },
  },
}
</script>

<style>
.walkthrough-designer .w-text-editor {
  @apply rounded bg-white border border-gray-300;
}
.walkthrough-designer .main-modal .ehsdialog-content {
  @apply bg-gray-100;
}
.walkthrough-designer .w-text-editor .menubar__button.is-active {
  background-color: rgba(0, 0, 0, 0.1);
}
.walkthrough-designer .w-text-editor .menubar button[title='Insert image'],
.walkthrough-designer .w-text-editor .menubar button[data-w-id='timestamp'],
.walkthrough-designer .w-text-editor .menubar button[data-w-id='maximize'],
.walkthrough-designer .w-text-editor .menubar button[data-w-id='code'],
.walkthrough-designer .w-text-editor .menubar button[data-w-id='paragraph'],
.walkthrough-designer .w-text-editor .menubar button[data-w-id='ordered-list'],
.walkthrough-designer .w-text-editor .menubar button[data-w-id='code-block'],
.walkthrough-designer .w-text-editor .menubar button[data-w-id='strike'],
.walkthrough-designer .w-text-editor .menubar button[data-w-id='blockquote'],
.walkthrough-designer .w-text-editor .menubar button[title='Create table'] {
  display: none;
}
.walkthrough-designer .w-text-editor__content-wrapper {
  min-height: 5rem;
  max-height: 20rem;
}
.walkthrough-designer .w-form__content {
  @apply px-10;
}
.save-button {
  @apply m-1;
}
.save-button-label {
  @apply mr-1;
}
/* Wizer's select-all deselect-all datatable functionality appears to be broken
  hiding the buttons for now */
.walkthrough-designer .w-data-table-header__select .w-checkbox {
  visibility: hidden;
}
.walkthrough-designer .w-data-table-toolbar div[data-w-id='undo-selection'] {
  display: none;
}
.walkthrough-designer .w-file-list__list--item {
  @apply items-center;
}
.walkthrough-designer .w-data-table,
.walkthrough-designer .ehs-data-table {
  @apply rounded bg-white border border-gray-300;
}
.walkthrough-designer .w-data-table-toolbar .w-toolbar,
.walkthrough-designer .ehs-data-table-toolbar .w-toolbar,
.walkthrough-designer .w-icon-button {
  @apply h-auto;
}
.walkthrough-designer .w-icon-button {
  @apply pt-1;
}
.walkthrough-designer .w-file-list__dropzone {
  @apply bg-white;
}
/* the link functionality does not work in roam so lets disable the dialog from ever showing */
.walkthrough-designer .menububble {
  display: none;
}
</style>
